import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import axios from "axios";
import Select, { components } from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faHospital,
  faShieldAlt,
  faArrowRight,
  faBookOpen,
  faSpinner,
  faStethoscope as faConsultIcon,
  faUserInjured as faInpatientIcon,
  faBandage as faOutpatientIcon,
  faVial as faDiagnosticIcon,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import MemberViewModal from "../Member/MemberViewModal";
import BatchFileSpecsModal from "../Member/BatchFileSpecsModal";
import SuccessModal from "./SuccessModal";
import GenderDisplay from "./getGenderDisplay";
import Papa from "papaparse"; // For CSV export

// Separated Components Imports
import ConfirmationModal from "./ConfirmationModal";
import TransactionSuccessModal from "./TransactionSuccessModal";
import Type1ContinueModal from "./Type1ContinueModal";
import Type2ContinueModal from "./Type2ContinueModal";
import LoadingModal from "./LoadingModal";
// import ActionButtons from "./ActionButtons"; // Assuming this is now separate
// import ProcedureTable from "./ProcedureTable"; // Assuming this is now separate
import PreSelectModal from "./PreSelectModal";
import CancelConfirmModal from "./CancelConfirmModal";
import Type3ContinueModal from "./Type3ContinueModal";
import FlowType1ConfirmationModal from "./FlowType1ConfirmationModal";
import FlowType2ConfirmationModal from "./FlowType2ConfirmationModal";
import FlowType4ContinueModal from "./FlowType4ContinueModal";
import {
  TRACKER_STORAGE_KEYS,
  publishTrackerLoaResult,
  readStorageObject,
} from "../../utils/trackerBridge";
import {
  getTransactionCancelLockReason,
  getTransactionEditLockReason,
} from "./transactionEditRules";
import { getDisplayLoaNo } from "./transactionLoaDisplayRules";

const MySwal = withReactContent(Swal);
const LOA_PDF_SPECS_ENDPOINT = `${BASE_URL}/generate-loa/loa-document/specs`;
const LOA_PDF_SPECS_PAGE = "/loa-pdf-generation-specs";

const isTruthyApprovalFlag = (value) =>
  value === true || String(value).trim().toLowerCase() === "true";

const buildApprovalMessage = (transNo) =>
  `TRN# ${transNo || "Transaction"} needs approval, please notify your TL/supervisor.`;

const tokenizedSelectFilter = (option, inputValue) => {
  const normalizedInput = String(inputValue || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

  if (!normalizedInput) {
    return true;
  }

  const searchableText = [
    option?.label,
    option?.value,
    option?.data?.text,
    option?.data?.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return normalizedInput
    .split(" ")
    .every((token) => searchableText.includes(token));
};

const resolveLoaDocumentUrl = (data = {}) => {
  const documentGeneration =
    data.loa_document_generation ||
    data.loaDocumentGeneration ||
    data.loa_pdf_generation ||
    data.loaPdfGeneration ||
    {};
  const generatedFile =
    documentGeneration.generated_file ||
    documentGeneration.generatedFile ||
    {};

  return (
    documentGeneration.signed_url ||
    documentGeneration.signedUrl ||
    documentGeneration.public_share_url ||
    documentGeneration.publicShareUrl ||
    generatedFile.public_share_url ||
    generatedFile.publicShareUrl ||
    generatedFile.signed_url ||
    generatedFile.signedUrl ||
    data.loa_document_url ||
    data.loaDocumentUrl ||
    ""
  );
};

const openLoaDocument = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const buildViewLoaDocumentAlertOptions = (url) =>
  url
    ? {
        confirmButtonText: "View LOA Document",
        showCancelButton: true,
        cancelButtonText: "Close",
        cancelButtonColor: "#6b7280",
        preConfirm: () => {
          openLoaDocument(url);
          return true;
        },
      }
    : {
        confirmButtonText: "OK",
      };

const getApprovalNotice = (payload, fallbackTransNo) => {
  const responseData = payload?.data || {};
  const summary = responseData.summary || payload?.summary || {};
  const approvalStepData = summary.step5?.data || {};
  const status =
    responseData.approval_status ||
    responseData.approvalStatus ||
    approvalStepData.res ||
    approvalStepData["@res"] ||
    "";
  const needsApproval =
    isTruthyApprovalFlag(responseData.needs_approval) ||
    isTruthyApprovalFlag(responseData.needsApproval) ||
    String(status).trim().toLowerCase() === "needs approval";
  const transNo =
    responseData.trans_no ||
    responseData.transNo ||
    payload?.trans_no ||
    payload?.transNo ||
    fallbackTransNo;

  return {
    needsApproval,
    status,
    message:
      responseData.approval_message ||
      responseData.approvalMessage ||
      (needsApproval ? buildApprovalMessage(transNo) : ""),
  };
};

const APPROVAL_INCLUDED_COVERAGES = [
  {
    code: "ATACCESS",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "ATACCESS-",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "EMERGENCY",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "FIRSTLIFE-UTIL-20220804",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "MATERNITY-BASIC-CONSULTATION",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "MATERNITY-CONSULTATION",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "MATERNITY-LABORATORY",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "MEDEXPRESS",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "MEDPADALA",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "MPSYCHE",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-BASIC-CONSULTATION",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-CLINIC-SETTING",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-FACILITY-FEE",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-LABORATORY",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-MEDPADALA",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-null",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-OPD-OR",
    timing: "Approval is checked after Continue Generate LOA.",
  },
  {
    code: "OUTPATIENT-REFERRAL-TO-SPECIALIST",
    timing: "Approval is checked after Continue Generate LOA.",
  },
];

const APPROVAL_EXCLUDED_COVERAGES = [
  { code: "RI-INPATIENT", reason: "Excluded from approval per current QA spec." },
  { code: "RI-OUPATIENT", reason: "Excluded from approval per current QA spec." },
  { code: "RI-OUTPATIENT", reason: "Excluded from approval per current QA spec." },
  { code: "INPATIENT-ADMISSION", reason: "Admission TRN flow is not routed to LOA approval." },
  { code: "MATERNITY-ADMISSION", reason: "Admission TRN flow is not routed to LOA approval." },
  { code: "APE", reason: "Excluded from approval per current QA spec." },
];

const APPROVAL_BYPASS_RULES = [
  "Settings bypass is active: settings.disable_check = 1 and current datetime is inside disable_start_dt to disable_end_dt.",
  "Issuer oauth user id is active in v2_loa_approval_bypass_users during the 06:00 to 00:59 pre-audit window.",
  "Current time is 01:00 to 05:59, treated as off-hours override.",
  "Coverage is listed under Not Included in Approval.",
];

const ApprovalSpecsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-y-0 left-0 z-[70] tracker-aware-modal-layer flex items-center justify-center bg-black/50 px-4 py-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 10 }}
          transition={{ duration: 0.18 }}
          className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Approval Specs</h2>
              <p className="mt-1 text-sm text-gray-600">
                Current approval behavior after Continue Generate LOA.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close approval specs"
            >
              x
            </button>
          </div>

          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-semibold">Pre-audit rule</p>
            <p className="mt-1">
              From 06:00 to 00:59, included LOA coverages are routed to approval
              after Continue Generate LOA unless the issuer has LOA approval bypass
              or the settings bypass window is active.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <section>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                Coverages Included in Approval
              </h3>
              <div className="space-y-3">
                {APPROVAL_INCLUDED_COVERAGES.map((coverage) => (
                  <div
                    key={coverage.code}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <p className="font-mono text-xs font-semibold text-gray-900">
                      {coverage.code}
                    </p>
                    <p className="mt-2 text-sm text-gray-600">{coverage.timing}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                Not Included in Approval
              </h3>
              <div className="space-y-3">
                {APPROVAL_EXCLUDED_COVERAGES.map((coverage) => (
                  <div
                    key={coverage.code}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
                  >
                    <p className="font-mono text-xs font-semibold text-gray-900">
                      {coverage.code}
                    </p>
                    <p className="mt-2">{coverage.reason}</p>
                  </div>
                ))}
              </div>

              <h3 className="mb-3 mt-5 text-lg font-semibold text-gray-900">
                Logic That Will Not Enter Approval
              </h3>
              <div className="space-y-3">
                {APPROVAL_BYPASS_RULES.map((rule) => (
                  <div
                    key={rule}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
                  >
                    {rule}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const calculateAge = (dateValue) => {
  if (!dateValue) return null;

  const birthDate = new Date(dateValue);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  return hasBirthdayPassed ? age : age - 1;
};

const GenerateLoaTransaction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [LoadingModalTitle, setLoadingModalTitle] = useState(
    "Generating LOA Transaction"
  );
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [showType2Modal, setShowType2Modal] = useState(false); // New for TYPE_2
  const [showType4Modal, setShowType4Modal] = useState(false); // New for TYPE_4
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showPreSelectModal, setShowPreSelectModal] = useState(false);
  const [showLoaPdfSpecsModal, setShowLoaPdfSpecsModal] = useState(false);
  const [showApprovalSpecsModal, setShowApprovalSpecsModal] = useState(false);
  const searchInputRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // New: Debounced search term for filtering
  const [isViewMemberModalOpen, setIsViewMemberModalOpen] = useState(false);
  const [isGenerateTRNSuccessModalopen, setIsGenerateTRNSuccessModal] =
    useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [selectedTransNo, setSelectedTransNo] = useState(null);
  const [selectedLoaNo, setSelectedLoaNo] = useState(null);
  const [hideGeneratedLoaNo, setHideGeneratedLoaNo] = useState(false);
  const [showFlowType1Confirm, setShowFlowType1Confirm] = useState(false);
  const [isFlowType1Submitting, setIsFlowType1Submitting] = useState(false);
  const [showFlowType2Confirm, setShowFlowType2Confirm] = useState(false);
  const [isFlowType2Submitting, setIsFlowType2Submitting] = useState(false);
  const toastRef = useRef(null);
  const [transactionData, setTransactionData] = useState(null);
  const [physicianOptions, setPhysicianOptions] = useState([]);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [physicianEntryMode, setPhysicianEntryMode] = useState("dropdown");
  const [freeTextPhysicianName, setFreeTextPhysicianName] = useState("");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [loading, setLoading] = useState({
    member: false,
    provider: false,
    coverage: false,
    physicians: false,
    type2: false, // New
  });
  const [memberOptions, setMemberOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [coverageOptions, setCoverageOptions] = useState([]);
  const [searchInput, setSearchInput] = useState({
    member: "",
    provider: "",
    coverage: "",
  });
  const [isFormReady, setIsFormReady] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [editLockReason, setEditLockReason] = useState("");
  const [cancelLockReason, setCancelLockReason] = useState("");
  const editPrefillLoadedRef = useRef(false);
  const trackerPrefillAppliedRef = useRef(false);

  const isFromTracker = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return (
      params.get("from_tracker") === "1" ||
      Boolean(location.state?.fromTracker)
    );
  }, [location.search, location.state]);

  const editTransactionSource = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const stateTransaction = location.state?.editTransaction || null;
    const transNo = stateTransaction?.trans_no || params.get("edit_trans_no");

    if (!transNo) return null;

    return {
      ...stateTransaction,
      trans_no: transNo,
    };
  }, [location.search, location.state]);

  const isEditMode = Boolean(editTransactionSource?.trans_no);
  const isEditActionLocked = Boolean(editLockReason);
  const isCancelActionLocked = Boolean(cancelLockReason);
  const displaySelectedLoaNo = useMemo(
    () =>
      getDisplayLoaNo(
        { ...(transactionData || {}), loa_no: selectedLoaNo },
        { hideRealLoaNo: hideGeneratedLoaNo }
      ),
    [hideGeneratedLoaNo, selectedLoaNo, transactionData]
  );

  // TYPE_2 specific states
  const [icdData, setIcdData] = useState([]);
  const [cptData, setCptData] = useState([]);
  const [ratesData, setRatesData] = useState([]);
  const [pkgsData, setPkgsData] = useState([]);
  const [selectedIcd, setSelectedIcd] = useState(null);
  const [selectedCpt, setSelectedCpt] = useState(null);
  const [cptRate, setCptRate] = useState(0);
  const [procedures, setProcedures] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [searchIcdTerm, setSearchIcdTerm] = useState("");
  const [searchCptTerm, setSearchCptTerm] = useState("");

  // TYPE_3
  const [showVocModal, setShowVocModal] = useState(false);

  // Add this ref at the top of your component (with other useRef/useState)
  const isTypingRef = useRef(false);

  // Flow Types Definition
  const FLOW_TYPES = useMemo(
    () => ({
      TYPE_1: [
        "OUTPATIENT-BASIC-CONSULTATION",
        "OUTPATIENT-REFERRAL-TO-SPECIALIST",
        "MATERNITY-BASIC-CONSULTATION",
      ],
      TYPE_2: [
        "OUTPATIENT-LABORATORY",
        "OUTPATIENT-CLINIC-SETTING",
        "OUTPATIENT-FACILITY-FEE",
        "EMERGENCY",
        "MATERNITY-LABORATORY",
      ],
      TYPE_3: [
        "INPATIENT-ADMISSION",
        "INPATIENT-DISCHARGE",
        "MATERNITY-ADMISSION",
        "MATERNITY-DISCHARGE",
      ],
      TYPE_4: ["OUTPATIENT-OPD-OR"],
      // Add more as needed
    }),
    []
  );

  const flowType = useMemo(() => {
    const coverageId = selectedCoverage?.id;
    if (FLOW_TYPES.TYPE_1.includes(coverageId)) return "TYPE_1";
    if (FLOW_TYPES.TYPE_2.includes(coverageId)) return "TYPE_2";
    if (FLOW_TYPES.TYPE_3.includes(coverageId)) return "TYPE_3";
    if (FLOW_TYPES.TYPE_4.includes(coverageId)) return "TYPE_4";
    return "TYPE_1"; // Default
  }, [selectedCoverage, FLOW_TYPES]);

  // Define categories for coverages
  const categories = useMemo(
    () => [
      { id: "all", label: "All Coverages", icon: faShieldAlt },
      {
        id: "consultation",
        label: "Consultation",
        icon: faConsultIcon,
        filter: (opt) =>
          opt?.text?.toLowerCase()?.includes("consult") ||
          opt?.text?.toLowerCase()?.includes("opd"),
      },
      {
        id: "inpatient",
        label: "Inpatient",
        icon: faInpatientIcon,
        filter: (opt) =>
          opt?.text?.toLowerCase()?.includes("ipd") ||
          opt?.text?.toLowerCase()?.includes("inpatient"),
      },
      {
        id: "outpatient",
        label: "Outpatient",
        icon: faOutpatientIcon,
        filter: (opt) =>
          opt?.text?.toLowerCase()?.includes("outpatient") &&
          !opt?.text?.toLowerCase()?.includes("consult"),
      },
      {
        id: "diagnostic",
        label: "Diagnostics",
        icon: faDiagnosticIcon,
        filter: (opt) =>
          opt?.text?.toLowerCase()?.includes("lab") ||
          opt?.text?.toLowerCase()?.includes("xray") ||
          opt?.text?.toLowerCase()?.includes("diagnostic"),
      },
    ],
    []
  );

  const customStyles = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        minHeight: "48px",
        backgroundColor: "white",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: state.isFocused
          ? "0 0 0 3px rgba(59, 130, 246, 0.1)"
          : "none",
        color: "#374151",
        "&:hover": { borderColor: "#3b82f6" },
        fontSize: "14px",
      }),
      input: (provided) => ({
        ...provided,
        color: "#374151",
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "#9ca3af",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: "#1f2937",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "#3b82f6"
          : state.isFocused
          ? "#f3f4f6"
          : "white",
        color: state.isSelected ? "white" : "#374151",
        fontSize: "14px",
        padding: "12px 16px",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: "white",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        marginTop: "4px",
      }),
      noOptionsMessage: (provided) => ({
        ...provided,
        color: "#6b7280",
        fontSize: "14px",
      }),
      loadingMessage: (provided) => ({
        ...provided,
        color: "#3b82f6",
        fontSize: "14px",
      }),
    }),
    []
  );

  const LoadingIndicator = (props) => (
    <components.LoadingIndicator {...props}>
      <FontAwesomeIcon icon={faSpinner} spin className="text-indigo-500 mr-2" />
    </components.LoadingIndicator>
  );

  const filteredCoverages = useMemo(() => {
    if (activeCategory === "all") return coverageOptions;
    const category = categories.find((cat) => cat.id === activeCategory);
    return category ? coverageOptions.filter(category.filter) : coverageOptions;
  }, [activeCategory, coverageOptions, categories]);

  // Updated: Use debouncedSearchTerm for filtering to reduce re-renders on every keystroke
  const finalFilteredCoverages = useMemo(() => {
    const term = debouncedSearchTerm?.toLowerCase();
    return filteredCoverages.filter(
      (cov) =>
        cov?.text?.toLowerCase()?.includes(term) ||
        cov?.id?.toLowerCase()?.includes(term)
    );
  }, [filteredCoverages, debouncedSearchTerm]);

  // New: Debounce the search term update to prevent frequent re-filters/re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const ensureOptionExists = useCallback((option, setter) => {
    if (!option?.id) return;

    setter((prev) => {
      const exists = prev.some(
        (item) => String(item.id) === String(option.id)
      );
      return exists ? prev : [option, ...prev];
    });
  }, []);

  const resolvePrefillOption = useCallback((incoming, options = []) => {
    if (!incoming) return null;

    const normalized =
      typeof incoming === "string" ? { id: incoming, text: incoming } : incoming;
    const id = normalized.id ?? normalized.value ?? normalized.code;
    if (!id) return null;

    const existing = options.find(
      (option) => String(option?.id) === String(id)
    );
    if (existing) return existing;

    return {
      id,
      text:
        normalized.text ||
        normalized.label ||
        normalized.name ||
        String(id),
    };
  }, []);

  const loadExistingTransactionForEdit = useCallback(
    async (sourceTransaction) => {
      const transNo = sourceTransaction?.trans_no;
      if (!transNo) return;

      setLoadingModalTitle("Loading Transaction Details");
      setIsGenerating(true);
      setShowPreSelectModal(false);

      try {
        const response = await axios.get(
          `${BASE_URL}/transactions/${encodeURIComponent(String(transNo))}/details`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );

        if (response.data.status !== "success") {
          toast.error(response.data.message || "Failed to load transaction.");
          return;
        }

        const details = response.data.data || {};
        const transaction = {
          ...sourceTransaction,
          ...details,
        };

        const memberParts = [
          transaction.corp_name,
          transaction.member_id,
          transaction.member_name || transaction.full_name,
        ].filter(Boolean);
        const memberOption = {
          id: transaction.member_id,
          text: memberParts.join(" | ") || transaction.member_name || "N/A",
        };
        const providerOption = {
          id: transaction.provider_code,
          text:
            transaction.provider_code && transaction.provider_name
              ? `[${transaction.provider_code}] ${transaction.provider_name}`
              : transaction.provider_name || transaction.provider_code || "N/A",
        };
        const coverageOption = {
          id: transaction.coverage_code,
          text:
            transaction.coverage_text ||
            (transaction.coverage_code
              ? `[${transaction.coverage_code}] ${
                  transaction.coverage_desc || transaction.coverage_code
                }`
              : "N/A"),
        };

        ensureOptionExists(memberOption, setMemberOptions);
        ensureOptionExists(providerOption, setProviderOptions);
        ensureOptionExists(coverageOption, setCoverageOptions);

        setSelectedMember(memberOption);
        setSelectedProvider(providerOption);
        setSelectedCoverage(coverageOption);
        setSelectedTransNo(transaction.trans_no);
        setSelectedLoaNo(transaction.loa_no);
        setHideGeneratedLoaNo(false);
        setTransactionData({
          ...transaction,
          plan_expiry: transaction.plan_expiry || transaction.expiry_date || "N/A",
          age: transaction.age || calculateAge(transaction.date_of_birth) || "N/A",
          date_issued:
            transaction.date_issued ||
            transaction.loa_date_issued ||
            transaction.trans_date ||
            "N/A",
          valid_until:
            transaction.valid_until ||
            transaction.expire_date ||
            transaction.expiry_date ||
            "N/A",
          physician_name:
            transaction.physician_name || transaction.doctor_name || "N/A",
          prof_fee: transaction.prof_fee || transaction.loa_amount || "N/A",
          chief_complaint: transaction.chief_complaint || "N/A",
          requested_lab:
            transaction.requested_lab ||
            transaction.requested_lab_procedure ||
            "N/A",
          special_instructions: transaction.special_instructions || "N/A",
          remarks: transaction.remarks || "null",
        });
        setChiefComplaint(transaction.chief_complaint || "");
        setEditLockReason(getTransactionEditLockReason(transaction));
        setCancelLockReason(getTransactionCancelLockReason(transaction));
        setShowTransactionModal(true);
        setIsGenerateTRNSuccessModal(false);
      } catch (error) {
        if (error.response?.status === 401) {
          window.location.href = "/login";
          return;
        }
        if (error.response?.status === 403) {
          toast.error("You do not have permission to access this.");
          return;
        }
        if (error.response?.status === 429) {
          toast.error("Rate limit exceeded. Please try again later.");
          return;
        }
        toast.error("Failed to load transaction for editing.");
      } finally {
        setIsGenerating(false);
        setLoadingModalTitle("Generating LOA Transaction");
      }
    },
    [ensureOptionExists]
  );

  const fetchInitialData = useCallback(async () => {
    setLoading({ member: true, provider: true, coverage: true });
    try {
      const [membersRes, providersRes, coveragesRes] = await Promise.all([
        axios
          .get(`${BASE_URL}/generate-loa/members/search`, {
            params: { q: "" },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          })
          .catch(() => ({ data: { status: "error", data: [] } })),
        axios
          .get(`${BASE_URL}/generate-loa/providers/search`, {
            params: { q: "" },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          })
          .catch(() => ({ data: { status: "error", data: [] } })),
        axios
          .get(`${BASE_URL}/generate-loa/coverages/search`, {
            params: { q: "" },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          })
          .catch(() => ({ data: { status: "error", data: [] } })),
      ]);
      if (membersRes.data.status == "success") {
        setMemberOptions(membersRes.data.data);
        // toast.success(`Loaded ${membersRes.data.data.length} initial members`, {
        //   duration: 2000,
        // });
      }
      if (providersRes.data.status == "success") {
        setProviderOptions(providersRes.data.data);
        // toast.success(
        //   `Loaded ${providersRes.data.data.length} initial providers`,
        //   { duration: 2000 }
        // );
      }
      if (coveragesRes.data.status == "success") {
        setCoverageOptions(coveragesRes.data.data);
        // toast.success(`Loaded all ${coveragesRes.data.data.length} coverages`, {
        //   duration: 2000,
        // });
      }
    } catch (error) {
      if (error.response?.status == 401) window.location.href = "/login";
      toast.error("Failed to load initial data. Try searching manually.");
    } finally {
      setLoading({ member: false, provider: false, coverage: false });
      setInitialLoaded(true);
      setShowPreSelectModal(!editTransactionSource?.trans_no && !isFromTracker);
    }
  }, [editTransactionSource?.trans_no, isFromTracker]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    editPrefillLoadedRef.current = false;
    setEditLockReason("");
    setCancelLockReason("");
  }, [editTransactionSource?.trans_no]);

  useEffect(() => {
    trackerPrefillAppliedRef.current = false;
  }, [location.key]);

  useEffect(() => {
    if (!isEditMode || !initialLoaded || editPrefillLoadedRef.current) {
      return;
    }

    editPrefillLoadedRef.current = true;
    loadExistingTransactionForEdit(editTransactionSource);
  }, [
    editTransactionSource,
    initialLoaded,
    isEditMode,
    loadExistingTransactionForEdit,
  ]);

  useEffect(() => {
    if (!isFromTracker || !initialLoaded || trackerPrefillAppliedRef.current) {
      return;
    }

    const envelope = readStorageObject(TRACKER_STORAGE_KEYS.loaPrefill);
    const prefill = envelope?.payload;
    if (!prefill) {
      return;
    }

    const memberOption = resolvePrefillOption(prefill.member, memberOptions);
    const providerOption = resolvePrefillOption(prefill.provider, providerOptions);
    const coverageOption = resolvePrefillOption(prefill.coverage, coverageOptions);

    if (memberOption) {
      ensureOptionExists(memberOption, setMemberOptions);
      setSelectedMember(memberOption);
    }
    if (providerOption) {
      ensureOptionExists(providerOption, setProviderOptions);
      setSelectedProvider(providerOption);
    }
    if (coverageOption) {
      ensureOptionExists(coverageOption, setCoverageOptions);
      setSelectedCoverage(coverageOption);
    }

    if (memberOption || providerOption || coverageOption) {
      trackerPrefillAppliedRef.current = true;
      setShowPreSelectModal(false);
    }
  }, [
    coverageOptions,
    ensureOptionExists,
    initialLoaded,
    isFromTracker,
    memberOptions,
    providerOptions,
    resolvePrefillOption,
  ]);

  useEffect(() => {
    setIsFormReady(
      !!selectedMember && !!selectedProvider && !!selectedCoverage
    );
  }, [selectedMember, selectedProvider, selectedCoverage]);

  // Reset TYPE_2 states when coverage changes
  useEffect(() => {
    if (flowType !== "TYPE_2") {
      setSelectedIcd(null);
      setSelectedCpt(null);
      setCptRate(0);
      setProcedures([]);
      setTotalAmount(0);
      setRemarks("");
    }
  }, [flowType]);

  const debouncedSearch = useMemo(() => {
    const timeoutIds = {};
    return (type, query, setter) => {
      clearTimeout(timeoutIds[type]);
      timeoutIds[type] = setTimeout(async () => {
        if (query.length < 1) return;
        setLoading((prev) => ({ ...prev, [type]: true }));
        try {
          const response = await axios.get(
            `${BASE_URL}/generate-loa/${type}s/search`,
            {
              params: { q: query },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            }
          );
          if (response.data.status == "success") {
            setter(response.data.data);
          } else {
            setter([]);
          }
        } catch (error) {
          if (error.response?.status == 401) window.location.href = "/login";
          setter([]);
        } finally {
          setLoading((prev) => ({ ...prev, [type]: false }));
        }
      }, 300);
    };
  }, []);

  const handleInputChange = useCallback(
    (type, setter) => (inputValue) => {
      setSearchInput((prev) => ({ ...prev, [type]: inputValue }));
      debouncedSearch(type, inputValue, setter);
    },
    [debouncedSearch]
  );

  const handleSelectChange = useCallback(
    (type) => (selected) => {
      if (type == "member") {
        setSelectedMember(selected);
      } else if (type == "provider") {
        setSelectedProvider(selected);
      } else if (type == "coverage") {
        setSelectedCoverage(selected);
      }
      if (selected) {
        toast.success(
          `${type.charAt(0).toUpperCase() + type.slice(1)} selected!`,
          { duration: 1500 }
        );
      }
    },
    []
  );

  const handlePreSelectCoverage = useCallback((coverage) => {
    setSelectedCoverage(coverage);
    setShowPreSelectModal(false);
    setSearchTerm("");
    toast.success("Coverage pre-selected! Ready to proceed.", {
      duration: 2000,
    });
  }, []);

  const handleSkipPreSelect = useCallback(() => {
    setShowPreSelectModal(false);
    setSearchTerm("");
    toast.info("Skipped pre-selection. You can choose coverage manually.", {
      duration: 2000,
    });
  }, []);

  const handleCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    setSearchTerm("");
  }, []);

  const handleNext = useCallback(() => {
    if (!isFormReady) {
      MySwal.fire({
        icon: "warning",
        title: "Almost There!",
        text: "Please select a Member, Provider, and Coverage to proceed.",
        footer: `<div class="text-sm mt-2">Progress: ${
          Object.values({
            member: !!selectedMember,
            provider: !!selectedProvider,
            coverage: !!selectedCoverage,
          }).filter(Boolean).length
        }/3</div>`,
        confirmButtonColor: "#3b82f6",
      });
      return;
    }
    setShowConfirmModal(true);
  }, [isFormReady, selectedMember, selectedProvider, selectedCoverage]);

  const handleGenerate = useCallback(async () => {
    setShowConfirmModal(false);
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/generate-loa/transactions`,
        {
          memberId: selectedMember.id,
          providerId: selectedProvider.id,
          coverageId: selectedCoverage.id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setIsGenerating(false);
      if (response.data.status == "success") {
        const responseData = response.data.data || {};
        const { trans_no, loa_no } = responseData;
        setSelectedTransNo(trans_no);
        setSelectedLoaNo(loa_no);
        setHideGeneratedLoaNo(true);
        setTransactionData({
          ...responseData,
          loa_no,
        });
        setShowTransactionModal(true);
        setIsGenerateTRNSuccessModal(true);

        publishTrackerLoaResult({
          tkt_num: readStorageObject(TRACKER_STORAGE_KEYS.loaPrefill)?.payload?.tkt_num,
          trans_no,
          loa_no: "",
          form_number: responseData?.form_no || responseData?.form_number || "",
          plan_code: responseData?.plan_code || "",
        });
      } else {
        MySwal.fire({
          icon: "error",
          title: "Unexpected Response!",
          text:
            response.data.message || "Generation completed but status unclear.",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      setIsGenerating(false);
      if (error.response?.status == 401) {
        window.location.href = "/login";
        return;
      }
      const formatErrorMessage = (msg) => {
        if (typeof msg == "string") {
          return msg;
        } else if (Array.isArray(msg)) {
          return msg
            .map((e) =>
              typeof e == "string" ? e : e.message || JSON.stringify(e)
            )
            .join(", ");
        } else if (msg && typeof msg == "object") {
          return Object.values(msg)
            .flat()
            .map((e) =>
              typeof e == "string" ? e : e.message || JSON.stringify(e)
            )
            .join(", ");
        } else {
          return JSON.stringify(msg || "Unknown error");
        }
      };
      let errorText = "Failed to generate LOA Transaction.";
      if (error.response?.status == 422) {
        const errorData = error.response.data;
        const isCoverageMismatch = Number(errorData?.data?.result) === 10;
        errorText = isCoverageMismatch
          ? `The selected member's plan does not include coverage for ${
              selectedCoverage?.text ||
              selectedCoverage?.id ||
              "the selected LOA coverage"
            }.`
          : formatErrorMessage(errorData.message);
        MySwal.fire({
          icon: "warning",
          title: isCoverageMismatch ? "Coverage Mismatch" : "Validation Failed!",
          text: errorText,
          confirmButtonColor: "#3b82f6",
        });
      } else {
        const data = error.response?.data;
        if (data && data.message) {
          errorText = formatErrorMessage(data.message);
        } else if (data) {
          errorText = JSON.stringify(data);
        }
        MySwal.fire({
          icon: "error",
          title: "Error!",
          text: errorText,
          confirmButtonColor: "#ef4444",
        });
      }
    }
  }, [selectedMember, selectedProvider, selectedCoverage]);

  const handleViewMemberInfo = useCallback(() => {
    if (selectedMember) {
      setSelectedMemberId(selectedMember.id);
      setIsViewMemberModalOpen(true);
    }
  }, [selectedMember]);

  // Conditional Continue Handler
  const handleContinue = useCallback(async () => {
    if (isEditActionLocked) {
      toast.error(editLockReason || "This transaction can no longer be edited.");
      return;
    }

    setShowTransactionModal(false);
    if (flowType === "TYPE_1") {
      setShowContinueModal(true);
      setShowFlowType1Confirm(false);
      setIsFlowType1Submitting(false);
      // Fetch affiliated physicians
      setLoading((prev) => ({ ...prev, physicians: true }));
      try {
        const response = await axios.get(
          `${BASE_URL}/generate-loa/providers/${selectedProvider?.id}/physicians`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data.status === "success") {
          setPhysicianOptions(response.data.data);
        }
      } catch (error) {
        toast.error("Failed to load physicians.");
      } finally {
        setLoading((prev) => ({ ...prev, physicians: false }));
      }
    } else if (flowType === "TYPE_2") {
      setShowType2Modal(true);
      // Load TYPE_2 data
      setLoading((prev) => ({ ...prev, type2: true }));
      try {
        const [icdsRes, cptsRes, ratesRes, pkgsRes] = await Promise.all([
          axios.get(`${BASE_URL}/generate-loa/icds`, {
            params: { q: "" }, // Load all
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }),
          axios.get(`${BASE_URL}/generate-loa/cpts`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }),
          axios.get(`${BASE_URL}/generate-loa/cpt-rates`, {
            params: { provider_code: selectedProvider?.id },
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }),
          axios.get(`${BASE_URL}/generate-loa/cpt-packages`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }),
        ]);

        if (icdsRes.data.status === "success") setIcdData(icdsRes.data.data);
        if (cptsRes.data.status === "success") setCptData(cptsRes.data.data);
        if (ratesRes.data.status === "success")
          setRatesData(ratesRes.data.data);
        if (pkgsRes.data.status === "success") setPkgsData(pkgsRes.data.data);

        toast.success("Loaded TYPE_2 data successfully.");
      } catch (error) {
        toast.error("Failed to load TYPE_2 data.");
      } finally {
        setLoading((prev) => ({ ...prev, type2: false }));
      }
    } else if (flowType === "TYPE_3") {
      setShowVocModal(true);
      // Load TYPE_3 VOC data (aggregated initial load)
      setLoading((prev) => ({ ...prev, voc: true })); // Add 'voc' to loading state if not present
      try {
        const response = await axios.get(
          `${BASE_URL}/voc/load-initial/${selectedTransNo}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        if (response.data.status === "success") {
          // Optionally set local state for VOC data if needed (e.g., pass to modal via props)
          toast.success("Loaded VOC data successfully.");
        }
      } catch (error) {
        toast.error("Failed to load VOC data.");
      } finally {
        setLoading((prev) => ({ ...prev, voc: false }));
      }
    } else if (flowType === "TYPE_4") {
      setShowType4Modal(true);
    }
  }, [
    editLockReason,
    flowType,
    isEditActionLocked,
    selectedProvider?.id,
    selectedTransNo,
  ]); // Add selectedTransNo to deps

  // // TYPE_2 Handlers
  // const handleIcdSearch = useCallback((inputValue) => {
  //   setSearchIcdTerm(inputValue);
  //   // Client-side filter for now, or debounce API call if needed
  //   // For simplicity, filter icdData
  // }, []);

  // const handleIcdSelect = useCallback((selected) => {
  //   setSelectedIcd(selected);
  // }, []);

  // const handleCptSearch = useCallback((inputValue) => {
  //   setSearchCptTerm(inputValue);
  // }, []);

  // const handleCptSelect = useCallback(
  //   (selected) => {
  //     setSelectedCpt(selected);
  //     if (selected && selected.cpt_code) {
  //       // Find rate for this CPT and provider
  //       const rateRow = ratesData.find(
  //         (r) =>
  //           r.cpt_code === selected.cpt_code &&
  //           r.provider_code === selectedProvider?.id
  //       );
  //       setCptRate(rateRow ? parseFloat(rateRow.cpt_rate) || 0 : 0);
  //     }
  //   },
  //   [ratesData, selectedProvider] // Changed deps to selectedProvider instead of selectedProvider.id
  // );

  const handleAddProcedure = useCallback(() => {
    // console.log(selectedCpt)
    if (selectedCpt && cptRate > 0) {
      // Check if CPT code already exists
      const isDuplicate = procedures.some(
        (procedure) => procedure.cpt.cpt_code === selectedCpt.cpt_code
      );

      if (isDuplicate) {
        MySwal.fire({
          icon: "error",
          title: "Already Added",
          html: `<strong>${selectedCpt.cpt_code}</strong> - ${selectedCpt.cpt_desc} is already in the list.`,
          confirmButtonColor: "#3085d6",
        });
        return; // Stop execution if duplicate
      }

      const newProcedure = {
        cpt: {
          ...selectedCpt,
          cpt_code: selectedCpt.cpt_code,
          cpt_desc: selectedCpt.cpt_desc,
          cpt_categ_code: selectedCpt.cpt_categ_code,
        },
        rate: cptRate,
      };

      setProcedures((prev) => [...prev, newProcedure]);
      setTotalAmount((prev) => prev + cptRate);
      setSelectedCpt(null);
      setCptRate(0);
    }
  }, [selectedCpt, cptRate, procedures]); // Don't forget to add 'procedures' to dependencies

  const handleRemoveProcedure = useCallback(
    (index) => {
      const removed = procedures[index];
      setProcedures((prev) => prev.filter((_, i) => i !== index));
      setTotalAmount((prev) => prev - removed.rate);
    },
    [procedures]
  );

  const handleEditRate = useCallback(
    (index, newRate) => {
      const diff = newRate - procedures[index].rate;
      setProcedures((prev) =>
        prev.map((p, i) => (i === index ? { ...p, rate: newRate } : p))
      );
      setTotalAmount((prev) => prev + diff);
    },
    [procedures]
  );

  const handleType2Submit = useCallback(() => {
    setShowFlowType2Confirm(true);
  }, []);

  const handleConfirmType2Proceed = useCallback(async () => {
    setIsFlowType2Submitting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/generate-loa/transactions/${selectedTransNo}/loa-type2`,
        {
          icd_code: selectedIcd?.value || "",
          procedures: procedures.map((p) => ({
            cpt_code: p.cpt?.cpt_code || p.cpt_code,
            rate: p.rate,
          })),
          total_amount: totalAmount,
          remarks,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      const payload = response.data || {};
      const data = payload.data || {};
      const success = payload.success ?? payload.status === "success";
      const approvalNotice = getApprovalNotice(payload, selectedTransNo);
      const message =
        approvalNotice.message ||
        data.message ||
        payload.message ||
        "LOA generated successfully for TYPE_2!";
      const memberName = data.member_name;
      const memberId = data.member_id;
      const transNo = data.trans_no;
      const loaNo = data.loa_no;
      const loaDocumentUrl = !approvalNotice.needsApproval
        ? resolveLoaDocumentUrl(data)
        : "";

      if (!success) {
        toast.error(
          typeof message === "string" ? message : JSON.stringify(message)
        );
        return;
      }

      // Surface step results or result codes if present
      const resultCode =
        payload.data?.result ||
        payload.data?.summary?.result ||
        payload.data?.summary?.step5?.data?.result;
      const baseMsg = typeof message === "string" ? message : JSON.stringify(message);
      const detailLines = [
        memberName ? `Member Name: ${memberName}` : null,
        memberId ? `Member ID: ${memberId}` : null,
        transNo ? `Transaction #: ${transNo}` : null,
        loaNo ? `LOA #: ${loaNo}` : null,
      ].filter(Boolean);
      const finalMsg = detailLines.length ? `${baseMsg}\n${detailLines.join("\n")}` : baseMsg;

      if (approvalNotice.needsApproval) {
        await MySwal.fire({
          icon: "info",
          title: "TRN Needs Approval",
          text: approvalNotice.message || buildApprovalMessage(transNo || selectedTransNo),
          confirmButtonColor: "#3b82f6",
          allowOutsideClick: false,
        });
        toast.info(approvalNotice.message || buildApprovalMessage(transNo || selectedTransNo));
      } else if (resultCode && [97, 98, 99, 100].includes(Number(resultCode))) {
        toast.warn(`Needs attention (code ${resultCode}): ${finalMsg}`);
      } else {
        await MySwal.fire({
          icon: "success",
          title: "LOA Generated!",
          text: finalMsg,
          confirmButtonColor: "#10b981",
          allowOutsideClick: false,
          ...buildViewLoaDocumentAlertOptions(loaDocumentUrl),
        });
        toast.success(finalMsg);
      }

      if (loaNo) {
        setSelectedLoaNo(loaNo);
      }
      setHideGeneratedLoaNo(Boolean(approvalNotice.needsApproval));
      setShowFlowType2Confirm(false);
      setShowType2Modal(false);
    } catch (error) {
      let errorMessage = "Failed to generate TYPE_2 LOA.";
      if (error.response?.data?.message) {
        errorMessage =
          typeof error.response.data.message === "string"
            ? error.response.data.message
            : JSON.stringify(error.response.data.message);
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsFlowType2Submitting(false);
    }
  }, [
    BASE_URL,
    selectedTransNo,
    selectedIcd,
    procedures,
    totalAmount,
    remarks,
  ]);

  const handleCancelConfirm = useCallback(() => {
    if (isCancelActionLocked) {
      toast.error(cancelLockReason || "This transaction can no longer be cancelled.");
      return;
    }

    setShowCancelConfirmModal(true);
  }, [cancelLockReason, isCancelActionLocked]);

  const handleTransactionModalClose = useCallback(() => {
    setShowTransactionModal(false);
    if (isEditMode) {
      navigate("/transactions");
    }
  }, [isEditMode, navigate]);

  const handleCancelTransaction = useCallback(async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      toast.error("Cancellation reason is required.");
      return;
    }
    if (trimmedReason.length < 2) {
      toast.error("Cancellation reason must be at least 2 characters long.");
      return;
    }
    if (trimmedReason.length > 1000) {
      toast.error("Cancellation reason cannot exceed 1000 characters.");
      return;
    }

    // const loadingSwal = MySwal.fire({
    //   title: "Cancelling...",
    //   text: "Please wait while we cancel the transaction.",
    //   allowOutsideClick: false,
    //   didOpen: () => {
    //     Swal.showLoading();
    //   },
    // });
    setIsGenerating(true);
    setLoadingModalTitle("Cancelling...");
    try {
      await axios.post(
        `${BASE_URL}/generate-loa/transactions/${selectedTransNo}/cancel`,
        { cancelled_details: trimmedReason }, // Use trimmed version
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      // MySwal.close(); // ✅ Close the loading Swal (use MySwal for consistency)
      MySwal.fire({
        icon: "success",
        title: "Cancelled!",
        text: `Transaction ${selectedTransNo} has been cancelled.`,
        confirmButtonColor: "#10b981",
      });
      setShowCancelConfirmModal(false);
      setCancelReason(""); // Reset after success
      // Close all possible open modals
      setShowTransactionModal(false);
      setShowContinueModal(false);
      setShowType2Modal(false); // New
      setIsGenerateTRNSuccessModal(false);
      setIsViewMemberModalOpen(false);
      setShowPreSelectModal(false);
      setShowConfirmModal(false);
      setIsGenerating(false);
      setLoadingModalTitle("Generating LOA Transaction");
    } catch (error) {
      MySwal.close();
      setIsGenerating(false);
      setLoadingModalTitle("Generating LOA Transaction");
      toast.error("Failed to cancel transaction.");
    }
  }, [selectedTransNo, cancelReason]);

  // const handleGenerateLoa = useCallback(async () => {
  //   // Implement for TYPE_1: POST with physician, chief_complaint
  //   try {
  //     const response = await axios.post(
  //       `${BASE_URL}/generate-loa/transactions/${selectedTransNo}/loa`,
  //       {
  //         physician_id: selectedPhysician?.tbl_id,
  //         chief_complaint: chiefComplaint,
  //         // Other fields
  //       },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem(
  //             "authentication/token"
  //           )}`,
  //         },
  //       }
  //     );
  //     if (response.data.status === "success") {
  //       toast.success("LOA generated!");
  //       setShowContinueModal(false);
  //       // Handle success
  //     }
  //   } catch (error) {
  //     toast.error("Failed to generate LOA.");
  //   }
  // }, [selectedPhysician, chiefComplaint, selectedTransNo]);

  const handleType1Submit = useCallback(() => {
    if (physicianEntryMode === "free_text") {
      if (!String(freeTextPhysicianName || "").trim()) {
        toast.error("Physician name is required.");
        return;
      }
    } else {
      if (!selectedPhysician) {
        toast.error("Physician is required.");
        return;
      }
    }
    if (!chiefComplaint.trim()) {
      toast.error("Chief complaint is required.");
      return;
    }
    if (chiefComplaint.length > 1000) {
      toast.error("Chief complaint cannot exceed 1000 characters.");
      return;
    }
    setShowFlowType1Confirm(true);
  }, [selectedPhysician, physicianEntryMode, freeTextPhysicianName, chiefComplaint]);

  const handleConfirmType1Proceed = useCallback(async () => {
    const buildDropdownPayload = (physician) => ({
      physicianEntryMode: "dropdown",
      physicianId: String(physician?.physician_id ?? physician?.tbl_id ?? physician?.id ?? ""),
      physicianName:
        physician?.full_name_w_id ||
        physician?.full_name ||
        physician?.name ||
        physician?.physician_name ||
        "",
      physicianPhilhealth:
        physician?.philhealth ??
        physician?.physician_philhealth ??
        null,
      physicianSpecialization:
        physician?.specialization ??
        physician?.physician_specialization ??
        "",
      chiefComplaint: chiefComplaint.trim(),
    });

    const buildFreeTextPayload = () => ({
      physicianEntryMode: "free_text",
      freeTextPhysicianName: String(freeTextPhysicianName || "").trim(),
      chiefComplaint: chiefComplaint.trim(),
    });

    const initialPayload =
      physicianEntryMode === "free_text"
        ? buildFreeTextPayload()
        : buildDropdownPayload(selectedPhysician);

    if (physicianEntryMode !== "free_text" && !initialPayload.physicianId) {
      toast.error("Physician is required.");
      return;
    }

    if (physicianEntryMode === "free_text" && !initialPayload.freeTextPhysicianName) {
      toast.error("Physician name is required.");
      return;
    }

    setLoading((prev) => ({ ...prev, physicians: true }));
    setIsGenerating(true);
    setIsFlowType1Submitting(true);

    const submitType1Payload = async (payload) => {
      const response = await axios.post(
        `${BASE_URL}/generate-loa/transactions/${selectedTransNo}/loa`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      const payloadData = response.data || {};
      const success =
        payloadData.status === "success" || payloadData.success === true;
      const data = payloadData.data || {};
      const approvalNotice = getApprovalNotice(payloadData, selectedTransNo);
      const needsApproval = approvalNotice.needsApproval;
      const loaDocumentUrl = !needsApproval ? resolveLoaDocumentUrl(data) : "";
      const message =
        approvalNotice.message ||
        data.message ||
        payloadData.message ||
        "LOA processed successfully!";

      if (!success) {
        toast.error(
          typeof message === "string" ? message : JSON.stringify(message)
        );
        return false;
      }

      setLoading((prev) => ({ ...prev, physicians: false }));
      setIsGenerating(false);
      setIsFlowType1Submitting(false);

      await MySwal.fire({
        icon: needsApproval ? "info" : "success",
        title: needsApproval ? "TRN Needs Approval" : "LOA Generated!",
        text: message,
        confirmButtonColor: needsApproval ? "#3b82f6" : "#10b981",
        allowOutsideClick: false,
        ...buildViewLoaDocumentAlertOptions(loaDocumentUrl),
      });
      if (data.loa_no) {
        setSelectedLoaNo(data.loa_no);
      }
      setHideGeneratedLoaNo(Boolean(needsApproval));
      setShowFlowType1Confirm(false);
      setShowContinueModal(false);
      setSelectedPhysician(null);
      setPhysicianEntryMode("dropdown");
      setFreeTextPhysicianName("");
      setChiefComplaint("");
      if (needsApproval) {
        toast.info(typeof message === "string" ? message : buildApprovalMessage(selectedTransNo));
      } else {
        toast.success(
          typeof message === "string" ? message : "LOA processed successfully!"
        );
      }

      return true;
    };

    try {
      await submitType1Payload(initialPayload);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate LOA.");
    } finally {
      setLoading((prev) => ({ ...prev, physicians: false }));
      setIsGenerating(false);
      setIsFlowType1Submitting(false);
    }
  }, [
    selectedPhysician,
    physicianEntryMode,
    freeTextPhysicianName,
    chiefComplaint,
    selectedTransNo,
  ]);

  // kapag naka-focus ka sa isang element (button o input) sa loob ng modal at pinindot mo ang Enter, automatic na ma-trigger yung onClick() ng currently focused element — parang native form submit behavior.
  useEffect(() => {
    if (!showPreSelectModal) {
      // Reset typing state when modal closes
      isTypingRef.current = false;
      setSearchTerm(""); // Clear search term on close
      return;
    }

    if (searchInputRef.current) {
      // Auto-focus the search input when modal opens
      searchInputRef.current.focus();
    }

    const handleEnterKey = (e) => {
      if (e.key === "Enter") {
        const activeElement = document.activeElement;
        if (activeElement && typeof activeElement.click === "function") {
          e.preventDefault(); // Prevent default form behavior
          activeElement.click();
        }
      }
    };

    document.addEventListener("keydown", handleEnterKey);

    // Cleanup: Aggressive cancel on unmount/change
    return () => {
      isTypingRef.current = false; // Unlock immediately
      document.removeEventListener("keydown", handleEnterKey);
      // Optional: If user starts typing manually, you could add onChange listener to force unlock
    };
  }, [showPreSelectModal]); // Keep dep as-is

  const showExitConfirmation = async (options = {}) => {
    const {
      transNo,
      onConfirm,
      title = "Exit Transaction?",
      message = "Your current progress will be lost",
      confirmText = "Exit Anyway",
      cancelText = "Continue Generate LOA",
    } = options;

    const result = await MySwal.fire({
      title: `
      <div class="flex flex-col items-center">
        <div class="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center mb-4 shadow-inner">
          <i class="fa-solid fa-right-from-bracket text-red-600 text-2xl"></i>
        </div>
        <h3 class="text-[1.25rem] font-semibold text-gray-900 leading-tight">${title}</h3>
      </div>
    `,
      html: `
      <div class="text-center mt-3 space-y-4 text-[0.95rem] text-gray-600">
        <p>${message}</p>
        ${
          transNo
            ? `
            <div class="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-indigo-50 to-white shadow-sm p-4">
              <div class="flex items-center gap-3 text-blue-700">
                <i class="fa-solid fa-pen-to-square text-blue-500 text-lg"></i>
                <div class="text-left">
                  <p class="font-medium text-sm">You can continue editing anytime</p>
                  <p class="text-xs mt-1 text-gray-500">Find TRN: 
                    <span class="font-mono bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md">${transNo}</span>
                  </p>
                </div>
              </div>
            </div>
          `
            : ""
        }
      </div>
    `,
      icon: false,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#f3f4f6",
      focusCancel: true,
      customClass: {
        popup:
          "rounded-3xl border border-gray-100 shadow-2xl p-6 backdrop-blur-md",
        confirmButton:
          "rounded-xl bg-red-600 text-white px-6 py-2.5 font-medium hover:bg-red-700 transition-all duration-200 hover:shadow-lg",
        cancelButton:
          "rounded-xl bg-gray-100 text-gray-700 px-6 py-2.5 font-medium hover:bg-gray-200 transition-all duration-200 hover:shadow",
        actions: "gap-4 mt-6",
      },
      buttonsStyling: false,
      showClass: {
        popup: "animate__animated animate__fadeInDown animate__faster",
      },
      hideClass: {
        popup: "animate__animated animate__fadeOutUp animate__faster",
      },
    });

    if (result.isConfirmed && onConfirm) {
      onConfirm();
    }

    return result.isConfirmed;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"
          animate={{
            x: [0, 200, 0],
            y: [0, 100, 0],
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"
          animate={{
            x: [0, -200, 0],
            y: [0, -100, 0],
          }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"
          animate={{
            x: [0, 100, 0],
            y: [0, 200, 0],
          }}
          transition={{ duration: 25, repeat: Infinity }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-8">
        <div className="mb-4 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowApprovalSpecsModal(true)}
            className="inline-flex items-center justify-center rounded-lg border border-amber-200 bg-white px-4 py-2.5 text-sm font-semibold text-amber-700 shadow-md transition-all duration-200 hover:bg-amber-50 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
          >
            <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-xs" />
            Approval Specs
          </button>
          <button
            type="button"
            onClick={() => setShowLoaPdfSpecsModal(true)}
            className="relative inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 shadow-md transition-all duration-200 hover:bg-indigo-50 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
          >
            <span className="absolute -right-2 -top-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white shadow">
              New
            </span>
            <FontAwesomeIcon icon={faBookOpen} className="mr-2 text-xs" />
            LOA PDF Generation Specs
          </button>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-center text-gray-800 mb-8 flex items-center justify-center gap-3"
        >
          <FontAwesomeIcon
            icon={faHospital}
            className="text-2xl text-blue-600"
          />
          Generate LOA Transaction
        </motion.h1>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>
              {
                Object.values({
                  member: !!selectedMember,
                  provider: !!selectedProvider,
                  coverage: !!selectedCoverage,
                }).filter(Boolean).length
              }
              /3
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <motion.div
              className="bg-gradient-to-r from-blue-600 to-indigo-600 h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{
                width: `${
                  (Object.values({
                    member: !!selectedMember,
                    provider: !!selectedProvider,
                    coverage: !!selectedCoverage,
                  }).filter(Boolean).length /
                    3) *
                  100
                }%`,
              }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Member Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-xl shadow-lg border border-gray-200"
          >
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} className="text-blue-600" />
              Select Member
            </label>
            <Select
              placeholder="Click to browse initial 20 members, or type to search (e.g., 'John Doe', 'MEM123')..."
              value={selectedMember}
              defaultValue={memberOptions[0] || null}
              onInputChange={handleInputChange("member", setMemberOptions)}
              onChange={handleSelectChange("member")}
              options={memberOptions}
              isLoading={loading.member}
              styles={customStyles}
              components={{ LoadingIndicator }}
              getOptionLabel={(option) => option.text}
              getOptionValue={(option) => option.id}
              filterOption={tokenizedSelectFilter}
            />
            {selectedMember && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs text-gray-600 mt-2 px-1 bg-green-50 rounded p-2"
              >
                {selectedMember.text}
              </motion.p>
            )}
          </motion.div>

          {/* Provider Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-xl shadow-lg border border-gray-200"
          >
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faHospital} className="text-green-600" />
              Select Provider
            </label>
            <Select
              placeholder="Click to browse initial 20 providers, or type to search (e.g., 'PROV001', 'Makati Med')..."
              value={selectedProvider}
              defaultValue={providerOptions[0] || null}
              onInputChange={handleInputChange("provider", setProviderOptions)}
              onChange={handleSelectChange("provider")}
              options={providerOptions}
              isLoading={loading.provider}
              styles={customStyles}
              components={{ LoadingIndicator }}
              getOptionLabel={(option) => option.text}
              getOptionValue={(option) => option.id}
            />
            {selectedProvider && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs text-gray-600 mt-2 px-1 bg-green-50 rounded p-2"
              >
                {selectedProvider.text}
              </motion.p>
            )}
          </motion.div>

          {/* Coverage Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-xl shadow-lg border border-gray-200"
          >
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faShieldAlt} className="text-purple-600" />
              Select Coverage
            </label>
            <Select
              placeholder="Click to browse all coverages, or type to search (e.g., 'IPD', 'Outpatient')..."
              value={selectedCoverage}
              defaultValue={coverageOptions[0] || null}
              onInputChange={handleInputChange("coverage", setCoverageOptions)}
              onChange={handleSelectChange("coverage")}
              options={coverageOptions}
              isLoading={loading.coverage}
              styles={customStyles}
              components={{ LoadingIndicator }}
              getOptionLabel={(option) => option.text}
              getOptionValue={(option) => option.id}
            />
            {selectedCoverage && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs text-gray-600 mt-2 px-1 bg-green-50 rounded p-2"
              >
                {selectedCoverage.text}
              </motion.p>
            )}
          </motion.div>
        </div>

        {/* Next Button */}
        <div className="text-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNext}
            disabled={!isFormReady}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
          >
            <FontAwesomeIcon
              icon={isFormReady ? faArrowRight : faSpinner}
              spin={!isFormReady}
            />
            {isFormReady ? "Proceed to Generate TRN" : "Complete Selections"}
          </motion.button>
        </div>
      </div>

      {/* Inline Modals */}
      {/* <PreSelectModal /> */}
      <PreSelectModal
        showPreSelectModal={showPreSelectModal}
        setShowPreSelectModal={setShowPreSelectModal}
        activeCategory={activeCategory}
        handleCategoryChange={handleCategoryChange}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        finalFilteredCoverages={finalFilteredCoverages}
        handlePreSelectCoverage={handlePreSelectCoverage}
        handleSkipPreSelect={handleSkipPreSelect}
        searchInputRef={searchInputRef}
        categories={categories}
      />

      <CancelConfirmModal
        showCancelConfirmModal={showCancelConfirmModal}
        setShowCancelConfirmModal={setShowCancelConfirmModal}
        selectedTransNo={selectedTransNo}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        handleCancelTransaction={handleCancelTransaction}
      />

      {/* Separated Modals */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleGenerate}
        selectedMember={selectedMember}
        selectedProvider={selectedProvider}
        selectedCoverage={selectedCoverage}
        isGenerating={isGenerating}
      />

      <TransactionSuccessModal
        isOpen={showTransactionModal}
        onClose={handleTransactionModalClose}
        selectedMember={selectedMember}
        selectedProvider={selectedProvider}
        selectedCoverage={selectedCoverage}
        selectedTransNo={selectedTransNo}
        selectedLoaNo={displaySelectedLoaNo}
        transactionData={transactionData}
        onViewMemberInfo={handleViewMemberInfo}
        onContinue={handleContinue}
        onCancel={handleCancelConfirm}
        flowType={flowType}
        continueDisabled={isEditActionLocked}
        cancelDisabled={isCancelActionLocked}
        disabledReason={cancelLockReason}
      />

      <Type1ContinueModal
        isOpen={showContinueModal && flowType === "TYPE_1"}
        onClose={async () => {
          const shouldClose = await showExitConfirmation({
            transNo: selectedTransNo,
            onConfirm: () => setShowContinueModal(false),
          });
        }}
        selectedProvider={selectedProvider}
        selectedCoverage={selectedCoverage}
        transactionData={transactionData}
        physicianOptions={physicianOptions}
        selectedPhysician={selectedPhysician}
        setSelectedPhysician={setSelectedPhysician}
        physicianEntryMode={physicianEntryMode}
        setPhysicianEntryMode={setPhysicianEntryMode}
        freeTextPhysicianName={freeTextPhysicianName}
        setFreeTextPhysicianName={setFreeTextPhysicianName}
        chiefComplaint={chiefComplaint}
        setChiefComplaint={setChiefComplaint}
        onGenerateLoa={handleType1Submit}
        loading={loading.physicians}
      />

      <Type2ContinueModal
        isOpen={showType2Modal && flowType === "TYPE_2"}
        onClose={async () => {
          const shouldClose = await showExitConfirmation({
            transNo: selectedTransNo,
            onConfirm: () => setShowType2Modal(false),
          });
        }}
        selectedCoverage={selectedCoverage}
        selectedProvider={selectedProvider}
        icdData={icdData}
        cptData={cptData}
        ratesData={ratesData}
        selectedIcd={selectedIcd}
        setSelectedIcd={setSelectedIcd}
        selectedCpt={selectedCpt}
        setSelectedCpt={setSelectedCpt}
        cptRate={cptRate}
        setCptRate={setCptRate}
        procedures={procedures}
        totalAmount={totalAmount}
        remarks={remarks}
        setRemarks={setRemarks}
        searchIcdTerm={searchIcdTerm}
        setSearchIcdTerm={setSearchIcdTerm}
        searchCptTerm={searchCptTerm}
        setSearchCptTerm={setSearchCptTerm}
        onAddProcedure={handleAddProcedure}
        onRemoveProcedure={handleRemoveProcedure}
        onEditRate={handleEditRate}
        onType2Submit={handleType2Submit}
        loading={loading.type2}
      />

      <FlowType4ContinueModal
        isOpen={showType4Modal && flowType === "TYPE_4"}
        onClose={async () => {
          const shouldClose = await showExitConfirmation({
            transNo: selectedTransNo,
            onConfirm: () => setShowType4Modal(false),
          });
        }}
        onForceClose={() => setShowType4Modal(false)}
        onCancel={handleCancelConfirm}
        transNo={selectedTransNo}
        providerId={selectedProvider?.id}
        selectedCoverage={selectedCoverage}
      />

      <FlowType1ConfirmationModal
        isOpen={showFlowType1Confirm}
        onClose={() => setShowFlowType1Confirm(false)}
        onProceed={handleConfirmType1Proceed}
        loading={isFlowType1Submitting}
        summaryData={{
          memberName: selectedMember?.text,
          providerName: selectedProvider?.text,
          coverageName: selectedCoverage?.text,
          transNo: selectedTransNo,
          corpName: transactionData?.corp_name,
          planExpiry: transactionData?.plan_expiry,
          gender: transactionData?.gender,
          age: transactionData?.age,
          dateIssued: transactionData?.date_issued,
          validUntil: transactionData?.valid_until,
          physicianName:
            physicianEntryMode === "free_text"
              ? freeTextPhysicianName
              : selectedPhysician?.full_name_w_id ||
                selectedPhysician?.full_name ||
                selectedPhysician?.name ||
                selectedPhysician?.physician_name ||
                transactionData?.physician_name,
          physicianEntryMode,
          profFee: transactionData?.prof_fee,
          chiefComplaint: chiefComplaint,
        }}
      />

      <FlowType2ConfirmationModal
        isOpen={showFlowType2Confirm}
        onClose={() => setShowFlowType2Confirm(false)}
        onProceed={handleConfirmType2Proceed}
        loading={isFlowType2Submitting}
        summaryData={{
          memberName: selectedMember?.text,
          providerName: selectedProvider?.text,
          coverageName: selectedCoverage?.text,
          transNo: selectedTransNo,
          corpName: transactionData?.corp_name,
          planExpiry: transactionData?.plan_expiry,
          gender: transactionData?.gender,
          age: transactionData?.age,
          dateIssued: transactionData?.date_issued,
          validUntil: transactionData?.valid_until,
          physicianName: transactionData?.physician_name,
          profFee: transactionData?.prof_fee,
          chiefComplaint: transactionData?.chief_complaint,
          requestedLab: transactionData?.requested_lab,
        }}
        type2Inputs={{
          icd: selectedIcd,
          procedures,
          totalAmount,
          remarks,
        }}
      />

      {/* TYPE_3. voc means verification of coverable */}
      <Type3ContinueModal
        isOpen={showVocModal}
        // onClose={() => setShowVocModal(false)}
        onClose={async () => {
          const shouldClose = await showExitConfirmation({
            transNo: selectedTransNo,
            onConfirm: () => setShowVocModal(false),
          });
        }}
        transNo={selectedTransNo}
      />

      <LoadingModal isOpen={isGenerating} title={LoadingModalTitle} />

      {/* External Modals */}
      {isViewMemberModalOpen && (
        <MemberViewModal
          memberId={selectedMemberId}
          onClose={() => setIsViewMemberModalOpen(false)}
        />
      )}

      {isGenerateTRNSuccessModalopen && (
        <SuccessModal
          isOpen={isGenerateTRNSuccessModalopen}
          onClose={() => setIsGenerateTRNSuccessModal(false)}
          transNo={selectedTransNo}
          loaNo={displaySelectedLoaNo}
          fromTracker={isFromTracker}
          onBackToTracker={() => {
            const returnTo =
              readStorageObject(TRACKER_STORAGE_KEYS.loaPrefill)?.payload?.returnTo ||
              "/tracker";
            setIsGenerateTRNSuccessModal(false);
            navigate(returnTo);
          }}
        />
      )}

      {/* Tooltips */}
      <Tooltip id="back-tooltip" place="top" />
      <Tooltip id="next-tooltip" place="top" />
      <BatchFileSpecsModal
        endpoint={LOA_PDF_SPECS_ENDPOINT}
        isOpen={showLoaPdfSpecsModal}
        onClose={() => setShowLoaPdfSpecsModal(false)}
        title="LOA PDF Generation Specs"
        fullPagePath={LOA_PDF_SPECS_PAGE}
        fullPageLabel="View full page"
      />
      <ApprovalSpecsModal
        isOpen={showApprovalSpecsModal}
        onClose={() => setShowApprovalSpecsModal(false)}
      />
      <ToastContainer />
    </div>
  );
};

export default GenerateLoaTransaction;
